import React, { useState, useEffect } from 'react';
import { backgroundSyncService } from '../services/backgroundSyncService';
import { predictivePreloader } from '../services/predictivePreloader';
import { intelligentRetryService } from '../services/intelligentRetryService';
import { progressiveEnhancement } from '../services/progressiveEnhancement';

const AdvancedRecoveryDashboard: React.FC = () => {
  const [syncStats, setSyncStats] = useState<any>(null);
  const [preloadStats, setPreloadStats] = useState<any>(null);
  const [retryStats, setRetryStats] = useState<any>(null);
  const [capabilities, setCapabilities] = useState<any>(null);
  const [enhancements, setEnhancements] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const updateStats = () => {
      setSyncStats(backgroundSyncService.getTaskStats());
      setPreloadStats(predictivePreloader.getPreloadStats());
      setRetryStats(intelligentRetryService.getRetryStats());
      setCapabilities(progressiveEnhancement.getCapabilities());
      setEnhancements(progressiveEnhancement.getEnhancementStatus());
    };

    updateStats();
    const interval = setInterval(updateStats, 5000);
    return () => clearInterval(interval);
  }, []);

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-16 right-4 bg-purple-600 text-white p-2 rounded-full shadow-lg hover:bg-purple-700 z-50"
        title="Advanced Recovery Dashboard"
      >
        🔧
      </button>
    );
  }

  return (
    <div className="fixed bottom-16 right-4 bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-96 z-50 max-h-96 overflow-y-auto">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-gray-800">Advanced Recovery</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      {/* System Capabilities */}
      {capabilities && (
        <div className="mb-4 p-3 bg-gray-50 rounded">
          <h4 className="text-sm font-medium mb-2">Sistem Yetenekleri</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>Memory: <span className="font-medium">{capabilities.memory}</span></div>
            <div>Network: <span className="font-medium">{capabilities.network}</span></div>
            <div>Device: <span className="font-medium">{capabilities.device}</span></div>
            <div>Performance: <span className="font-medium">{capabilities.performance}</span></div>
          </div>
        </div>
      )}

      {/* Background Sync Stats */}
      {syncStats && (
        <div className="mb-4 p-3 bg-blue-50 rounded">
          <h4 className="text-sm font-medium mb-2">Background Sync</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>Total: {syncStats.total}</div>
            <div>Pending: {syncStats.pending}</div>
            <div>Running: {syncStats.running}</div>
            <div>Failed: {syncStats.failed}</div>
          </div>
          <div className="mt-2 flex gap-1">
            <button
              onClick={() => backgroundSyncService.pauseSync()}
              className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded"
            >
              Pause
            </button>
            <button
              onClick={() => backgroundSyncService.resumeSync()}
              className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded"
            >
              Resume
            </button>
          </div>
        </div>
      )}

      {/* Predictive Preloader Stats */}
      {preloadStats && (
        <div className="mb-4 p-3 bg-green-50 rounded">
          <h4 className="text-sm font-medium mb-2">Predictive Preloader</h4>
          <div className="text-xs space-y-1">
            <div>Rules: {preloadStats.rulesCount}</div>
            <div>Patterns: {preloadStats.patternsCount}</div>
            <div>Preloaded: {preloadStats.preloadedRoutes.length}</div>
            <div>Status: {preloadStats.isEnabled ? '✅ Enabled' : '❌ Disabled'}</div>
          </div>
          <div className="mt-2 flex gap-1">
            <button
              onClick={() => predictivePreloader.enable()}
              className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded"
            >
              Enable
            </button>
            <button
              onClick={() => predictivePreloader.disable()}
              className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded"
            >
              Disable
            </button>
          </div>
        </div>
      )}

      {/* Retry Stats */}
      {retryStats && (
        <div className="mb-4 p-3 bg-yellow-50 rounded">
          <h4 className="text-sm font-medium mb-2">Intelligent Retry</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>Active: {retryStats.activeOperations}</div>
            <div>Attempts: {retryStats.totalAttempts}</div>
            <div>Failed: {retryStats.failedOperations}</div>
            <div>Recovered: {retryStats.successfulRetries}</div>
          </div>
        </div>
      )}

      {/* Active Enhancements */}
      {enhancements && (
        <div className="mb-4 p-3 bg-purple-50 rounded">
          <h4 className="text-sm font-medium mb-2">Active Enhancements</h4>
          <div className="space-y-1 text-xs">
            {Object.entries(enhancements).map(([key, isActive]) => (
              <div key={key} className="flex justify-between">
                <span>{key.replace(/_/g, ' ')}</span>
                <span>{isActive ? '✅' : '❌'}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-1">
            <button
              onClick={() => progressiveEnhancement.adjustQuality('low')}
              className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded"
            >
              Low
            </button>
            <button
              onClick={() => progressiveEnhancement.adjustQuality('medium')}
              className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded"
            >
              Medium
            </button>
            <button
              onClick={() => progressiveEnhancement.adjustQuality('high')}
              className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded"
            >
              High
            </button>
          </div>
        </div>
      )}

      {/* Emergency Controls */}
      <div className="flex gap-2">
        <button
          onClick={() => progressiveEnhancement.enableEmergencyMode()}
          className="flex-1 text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
        >
          Emergency Mode
        </button>
        <button
          onClick={() => {
            backgroundSyncService.clearCompletedTasks();
            predictivePreloader.clearPreloadCache();
            intelligentRetryService.clearOldContexts();
          }}
          className="flex-1 text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700"
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

export default AdvancedRecoveryDashboard;
